import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { updateEmail, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';

function Settings({
	user,
	projects,
	todos,
	taskCounts,
	onClose,
	onDeleteProject,
	onClearProjectTasks,
	onRenameProject,
	onStartOver,
	procrastinationCount
}) {
	const [activeSection, setActiveSection] = useState('projects');
	const [newEmail, setNewEmail] = useState('');
	const [password, setPassword] = useState('');
	const [emailError, setEmailError] = useState('');
	const [emailSuccess, setEmailSuccess] = useState('');
	const [selectedProject, setSelectedProject] = useState(null);
	const [newProjectName, setNewProjectName] = useState('');
	const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
	const [deleteProjectConfirm, setDeleteProjectConfirm] = useState(null); // { projectId, projectName }
	const [clearProjectConfirm, setClearProjectConfirm] = useState(null); // { projectId, projectName }

	const handleChangeEmail = async (e) => {
		e.preventDefault();
		setEmailError('');
		setEmailSuccess('');

		try {
			// Re-authenticate user before changing email
			const credential = EmailAuthProvider.credential(user.email, password);
			await reauthenticateWithCredential(auth.currentUser, credential);

			// Update email
			await updateEmail(auth.currentUser, newEmail);
			setEmailSuccess('Email updated successfully!');
			setNewEmail('');
			setPassword('');
		} catch (error) {
			console.error('Error updating email:', error);
			setEmailError(error.message);
		}
	};

	const handleRenameProject = (projectId) => {
		if (newProjectName.trim()) {
			onRenameProject(projectId, newProjectName);
			setSelectedProject(null);
			setNewProjectName('');
		}
	};

	const handleExportCSV = async () => {
		try {
			// Fetch all todos for the user across all projects
			const todosQuery = query(
				collection(db, 'todos'),
				where('userId', '==', user.uid)
			);
			const todosSnapshot = await getDocs(todosQuery);
			const allTodos = todosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

			const csvContent = [
				['Project', 'Task', 'Status', 'Completed'],
				...allTodos.map(todo => {
					const project = projects.find(p => p.id === todo.projectId);
					return [
						project?.name || 'Unknown',
						todo.text,
						todo.tab,
						todo.isCompleted ? 'Yes' : 'No'
					];
				})
			]
				.map(row => row.map(cell => `"${cell}"`).join(','))
				.join('\n');

			const blob = new Blob([csvContent], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = 'brutus-tasks.csv';
			a.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error exporting CSV:', error);
		}
	};

	const totalUndoneTasks = todos.filter(t => !t.isCompleted).length;

	return (
		<>
			<div className='modal-overlay' onClick={onClose} />
			<div className='settings-modal'>
				<button className='close-btn' onClick={onClose}>×</button>
				<h2>SETTINGS</h2>

				<div className='settings-nav'>
					<button
						className={activeSection === 'projects' ? 'active' : ''}
						onClick={() => setActiveSection('projects')}
					>
						Manage Projects
					</button>
					<button
						className={activeSection === 'stats' ? 'active' : ''}
						onClick={() => setActiveSection('stats')}
					>
						Stats
					</button>
					<button
						className={activeSection === 'data' ? 'active' : ''}
						onClick={() => setActiveSection('data')}
					>
						Data
					</button>
					<button
						className={activeSection === 'about' ? 'active' : ''}
						onClick={() => setActiveSection('about')}
					>
						About
					</button>
				</div>

				<div className='settings-content'>
					{activeSection === 'projects' && (
						<div className='settings-section'>
							<h3>Manage Projects</h3>
							{projects.length === 0 ? (
								<p>No projects yet.</p>
							) : (
								<ul className='project-list'>
									{projects.map(project => (
										<li key={project.id} className='project-item'>
											{selectedProject === project.id ? (
												<div className='rename-form'>
													<input
														type='text'
														value={newProjectName}
														onChange={(e) => setNewProjectName(e.target.value)}
														placeholder='New project name'
													/>
													<button onClick={() => handleRenameProject(project.id)}>
														Save
													</button>
													<button onClick={() => {
														setSelectedProject(null);
														setNewProjectName('');
													}}>
														Cancel
													</button>
												</div>
											) : (
												<>
													<span className='project-name'>{project.name}</span>
													<div className='project-actions'>
														<button onClick={() => {
															setSelectedProject(project.id);
															setNewProjectName(project.name);
														}}>
															Rename
														</button>
														<button onClick={() => setClearProjectConfirm({ projectId: project.id, projectName: project.name })}>
															Clear Tasks
														</button>
														<button
															className='delete-btn-red'
															onClick={() => setDeleteProjectConfirm({ projectId: project.id, projectName: project.name })}
														>
															Delete
														</button>
													</div>
												</>
											)}
										</li>
									))}
								</ul>
							)}
						</div>
					)}

					{activeSection === 'about' && (
						<div className='settings-section'>
							<h3>Your Account</h3>
							<p>Email: {user?.email}</p>

							<div className='support-section'>
								<h3>About BRUTUS</h3>
								<p>BRUTUS is a brutal task manager that doesn't care about your feelings. It forces you to focus on one task at a time, preventing multitasking and task switching.</p>
								<p>Unlike other productivity apps, BRUTUS is opinionated and rigid in its approach - because that's what you need to actually get things done.</p>
							</div>

							<div className='support-section'>
								<h3>FAQ</h3>
								<p><strong>Why can I only work on one task at a time?</strong></p>
								<p>Because multitasking is a myth. BRUTUS forces you to focus on the first task in your NOW list before you can move on to anything else.</p>

								<p><strong>How does the Brain Dump work?</strong></p>
								<p>Dump all your tasks in Brain Dump without thinking. When ready, hit "Assign Tasks" and BRUTUS will organize them for you - putting one in NOW and the rest in LATER.</p>

								<p><strong>Can I skip a task?</strong></p>
								<p>Sort of. BRUTUS will ask if you're procrastinating. Be honest. If you say no, we assume that task no longer needs to be completed and it will be deleted.</p>
							</div>

							<div className='support-section'>
								<h3>Support</h3>
								<p>Need help? Contact us at: <a href='mailto:support@wildpixel.dev'>support@wildpixel.dev</a></p>
							</div>
						</div>
					)}

					{activeSection === 'stats' && (
						<div className='settings-section'>
							<h3>Your Stats</h3>
							<div className='stats-grid'>
								<div className='stat-item'>
									<div className='stat-value'>{totalUndoneTasks}</div>
									<div className='stat-label'>Undone Tasks</div>
								</div>
								<div className='stat-item'>
									<div className='stat-value'>{taskCounts.tasksCompletedEver}</div>
									<div className='stat-label'>Tasks Completed (Lifetime)</div>
								</div>
								<div className='stat-item'>
									<div className='stat-value'>{procrastinationCount || 0}</div>
									<div className='stat-label'>Procrastination Attempts</div>
								</div>
							</div>
						</div>
					)}

					{activeSection === 'data' && (
						<div className='settings-section'>
							<h3>Export & Reset</h3>
							<button onClick={handleExportCSV} className='export-btn'>
								Export All Tasks as CSV
							</button>

							<div className='danger-zone'>
								<h3>Danger Zone</h3>
								{!showStartOverConfirm ? (
									<button
										className='danger-btn'
										onClick={() => setShowStartOverConfirm(true)}
									>
										Start Over (Delete All)
									</button>
								) : (
									<div className='confirm-start-over'>
										<p>This will delete ALL projects and tasks.</p>
										<p><strong>Tasks cannot be recovered.</strong></p>
										<p>Please export your data before deleting if you want to keep a record.</p>
										<button
											className='danger-btn'
											onClick={() => {
												onStartOver();
												setShowStartOverConfirm(false);
												onClose();
											}}
										>
											I Understand - Delete Everything
										</button>
										<button onClick={() => setShowStartOverConfirm(false)}>
											Cancel
										</button>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Delete Project Confirmation Modal */}
			{deleteProjectConfirm && (
				<>
					<div
						className='modal-overlay'
						onClick={() => setDeleteProjectConfirm(null)}
						style={{ zIndex: 2001 }}
					/>
					<div className='confirmation-modal' style={{ zIndex: 2002 }}>
						<h2>DELETE THIS PROJECT?</h2>
						<p>"{deleteProjectConfirm.projectName}"</p>
						<p className='warning-text'>
							This will delete the project and all its tasks. This action cannot be undone.
						</p>
						<div className='modal-buttons'>
							<button
								onClick={() => setDeleteProjectConfirm(null)}
								className='modal-btn cancel-btn'
							>
								CANCEL
							</button>
							<button
								onClick={() => {
									onDeleteProject(deleteProjectConfirm.projectId);
									setDeleteProjectConfirm(null);
								}}
								className='modal-btn confirm-btn'
							>
								YES, DELETE IT
							</button>
						</div>
					</div>
				</>
			)}

			{/* Clear Project Tasks Confirmation Modal */}
			{clearProjectConfirm && (
				<>
					<div
						className='modal-overlay'
						onClick={() => setClearProjectConfirm(null)}
						style={{ zIndex: 2001 }}
					/>
					<div className='confirmation-modal' style={{ zIndex: 2002 }}>
						<h2>CLEAR ALL TASKS?</h2>
						<p>"{clearProjectConfirm.projectName}"</p>
						<p className='warning-text'>
							This will delete all tasks in this project. This action cannot be undone.
						</p>
						<div className='modal-buttons'>
							<button
								onClick={() => setClearProjectConfirm(null)}
								className='modal-btn cancel-btn'
							>
								CANCEL
							</button>
							<button
								onClick={() => {
									onClearProjectTasks(clearProjectConfirm.projectId);
									setClearProjectConfirm(null);
								}}
								className='modal-btn confirm-btn'
							>
								YES, CLEAR ALL TASKS
							</button>
						</div>
					</div>
				</>
			)}
		</>
	);
}

export default Settings;
